import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { WASTE } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GapBadge, GapReasons, ReportStatus } from "@/components/waste/GapBadge";
import { ReportEditor, toPayload, PATHWAYS, EVIDENCE_KINDS } from "@/components/waste/ReportEditor";
import { fmtDateTime } from "@/lib/format";
import { Bot, Sparkles, Loader2, CheckCircle2, Undo2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

function AiSummary({ report, onChange }) {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState(report.ai_summary || "");
  useEffect(() => { setText(report.ai_summary || ""); }, [report.ai_summary]);
  const draft = async () => {
    setBusy(true);
    try { const r = await api.post(`/sub-reports/${report.id}/ai-summary`); onChange(r.data); toast.success("AI draft ready — review before sharing"); }
    catch (e) { toast.error(apiErr(e, "AI drafting failed")); }
    finally { setBusy(false); }
  };
  const save = async () => {
    try { const r = await api.patch(`/sub-reports/${report.id}`, { ai_summary: text }); onChange(r.data); toast.success("Improvement note saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="rounded-xl border border-sage-light p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Improvement note</p>
        <Button size="sm" variant="outline" onClick={draft} disabled={busy} data-testid={WASTE.aiDraftBtn}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-forest-mid" />} Draft with AI
        </Button>
      </div>
      {report.ai_summary_ai && (
        <div className="mb-2 flex items-start gap-2 rounded-r-lg border-l-4 border-amber-500 bg-amber-50/90 p-2">
          <Bot className="mt-0.5 h-4 w-4 text-amber-600" /><p className="text-xs text-amber-900"><b>AI-generated draft.</b> Review before sharing with the subcontractor.</p>
        </div>
      )}
      <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Summarise the gap and the improvements you expect…" data-testid={WASTE.aiText} />
      <Button size="sm" className="mt-2 bg-forest-mid hover:bg-forest-dark" onClick={save} data-testid={WASTE.aiSave}>Save note</Button>
    </div>
  );
}

export function ReviewDialog({ reportId, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [edit, setEdit] = useState(null);
  const [comment, setComment] = useState("");
  useEffect(() => {
    if (!reportId) { setR(null); return; }
    api.get(`/sub-reports/${reportId}`).then((x) => { setR(x.data); setEdit(x.data); }).catch((e) => toast.error(apiErr(e)));
  }, [reportId]);
  const update = (d) => { setR(d); setEdit(d); onChanged?.(); };
  const act = async (path, body) => {
    try { const x = await api.post(`/sub-reports/${r.id}/${path}`, body); update(x.data); toast.success("Done"); setComment(""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async () => {
    try { const x = await api.patch(`/sub-reports/${r.id}`, toPayload(edit)); update(x.data); toast.success("Saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const editable = r && !r.project_locked && r.entered_by_limelite && ["draft", "returned"].includes(r.status);
  return (
    <Dialog open={!!reportId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid={WASTE.review}>
        <DialogHeader><DialogTitle className="font-heading">Subcontractor report</DialogTitle></DialogHeader>
        {!r ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-forest-dark">{r.subcontractor_name}</p>
              <span className="text-sm text-muted-foreground">· {r.contract_name} · {r.period}</span>
              <ReportStatus status={r.status} testid={WASTE.reviewStatus} />
              <GapBadge status={r.gap.status} />
            </div>
            {r.entered_by_limelite && <p className="flex items-center gap-1.5 rounded-lg bg-sage-surface px-3 py-1.5 text-xs text-forest-dark"><ClipboardList className="h-3.5 w-3.5" /> Entered by Limelite ({r.entered_by_name}) on behalf of {r.subcontractor_name}</p>}
            <div className="rounded-xl border border-sage-light bg-sage-surface/50 p-3" data-testid={WASTE.gapCard}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sustainability gap</p>
              <GapReasons gap={r.gap} />
            </div>
            <ReportEditor value={edit} onChange={setEdit} pathways={PATHWAYS} kinds={EVIDENCE_KINDS} disabled={!editable} />
            {editable && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={saveEdit} data-testid={WASTE.saveEditBtn}>Save changes</Button>
                <Button className="bg-forest-mid hover:bg-forest-dark" onClick={() => act("submit")} data-testid={WASTE.submitBehalfBtn}>Submit for review</Button>
              </div>
            )}
            {r.comments?.length > 0 && (
              <div className="space-y-1">{r.comments.map((c) => <p key={c.id} className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900"><b>{c.by}</b> · {fmtDateTime(c.at)} — {c.text}</p>)}</div>
            )}
            {!r.project_locked && <AiSummary report={r} onChange={update} />}
            {r.project_locked && <p className="rounded-lg bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900">This project's completed report is issued — its reports are locked.</p>}
            {r.status === "submitted" && !r.project_locked && (
              <div className="space-y-2 rounded-xl border border-sage-light p-3">
                <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment for the subcontractor (required to return)" data-testid={WASTE.returnComment} />
                <div className="flex flex-wrap gap-2">
                  <Button className="bg-forest-mid hover:bg-forest-dark" onClick={() => act("accept")} data-testid={WASTE.acceptBtn}><CheckCircle2 className="mr-1.5 h-4 w-4" /> Accept</Button>
                  <Button variant="outline" disabled={!comment.trim()} onClick={() => act("return", { comment })} data-testid={WASTE.returnBtn}><Undo2 className="mr-1.5 h-4 w-4" /> Return for more information</Button>
                </div>
              </div>
            )}
            {r.status === "accepted" && <p className="text-xs text-muted-foreground">Accepted {fmtDateTime(r.accepted_at)} — locked.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
